import { isNull } from 'lodash';
import { type Layout } from 'react-grid-layout';
import { CodPosition, CodPositions } from 'src/constants/constants';
import {
  COD_PROMO_CONFIG,
  COD_THANK_YOU_SECTION,
  createCodDysPayload,
  createCODwithConfig,
  createDysComboPayload,
} from 'src/constants/gcfSectionConfig';
import {
  GlobalComponentsScreenInterface,
  IDisclaimerPosition,
  IDisclaimerWidth,
  SectionTypes,
} from 'src/interfaces';
import {
  calculateOffset,
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYS,
} from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

export const createSectionPayload = (
  sections: Layout[],
  gridHeight: number,
  screenWidth: number,
  rows: number,
  cols: number,
  extraHeight: number,
) =>
  sections.map((section) => ({
    height: Math.round((section.h * gridHeight) / rows),
    width: Math.round((section.w * screenWidth) / cols),
    topOffset: calculateOffset((section.y * gridHeight) / rows, extraHeight),
    leftOffset: Math.round((section.x * screenWidth) / cols),
    cols: section.w,
    rows: section.h,
  }));

export const createDisclaimerPayload = (
  disclaimerType: string | null,
  position: string | null,
  disclaimerHeight: number,
  disclaimerWidth: number,
  gridHeight: number,
) => {
  const topOffset = position === IDisclaimerPosition.TOP ? 0 : gridHeight;
  const payload: any[] = [];

  if (disclaimerType === IDisclaimerWidth.FULL) {
    payload.push({
      height: disclaimerHeight,
      width: disclaimerWidth,
      leftOffset: 0,
      topOffset,
    });
  } else if (disclaimerType === IDisclaimerWidth.HALF) {
    payload.push(
      {
        height: disclaimerHeight,
        width: disclaimerWidth,
        leftOffset: 0,
        topOffset,
      },
      {
        height: disclaimerHeight,
        width: disclaimerWidth,
        leftOffset: disclaimerWidth,
        topOffset,
      },
    );
  }

  return payload;
};

export const createCODPayload = (
  sectionTypes: SectionTypes[],
  screenIndex: string,
  disclaimerHeight: number,
  disclaimerPosition: string | null,
  codPosition: CodPosition = CodPositions.LEFT,
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) => {
  const codPayload: any[] = [];
  const disclaimerAdjustment =
    disclaimerPosition === IDisclaimerPosition.TOP ? disclaimerHeight : 0;
  const { hasCOD, hasDYS, hasDYP, hasThankYou } =
    checkSectionTypes(sectionTypes);

  const addCodEvent = (payload: any) => codPayload.push(payload);

  if (!globalComponentsScreen) return codPayload;

  const isLeftCodPosition =
    isNull(codPosition) || codPosition === CodPositions.LEFT;

  if (
    hasCOD &&
    isCurrentScreenWithCOD(Number(screenIndex), globalComponentsScreen)
  ) {
    addCodEvent(createCODwithConfig(disclaimerAdjustment, isLeftCodPosition));
    if (hasDYP) {
      addCodEvent({
        leftOffset: isLeftCodPosition ? 0 : 720,
        topOffset: disclaimerAdjustment,
        config: COD_PROMO_CONFIG,
        width: 360,
        height: 440,
      });
      addCodEvent({
        leftOffset: isLeftCodPosition ? 0 : 720,
        topOffset: 440 + disclaimerAdjustment,
        config: COD_PROMO_CONFIG,
        width: 360,
        height: 440,
      });
    }
  }

  if (
    hasDYS &&
    isCurrentScreenWithDYS(Number(screenIndex), globalComponentsScreen)
  ) {
    addCodEvent(createDysComboPayload(disclaimerAdjustment, isLeftCodPosition));
    addCodEvent(createCodDysPayload(disclaimerAdjustment, isLeftCodPosition));
  }

  if (
    hasThankYou &&
    isCurrentScreenWithCODThankYou(Number(screenIndex), globalComponentsScreen)
  ) {
    addCodEvent(COD_THANK_YOU_SECTION);
  }

  return codPayload;
};
